import {
  initGoogleTagManager as initGTM,
  firePageEvent,
  fireExperianPageViewEvent,
  fireContactEvent,
  fireJobEvent,
} from '@zr/common/js/gtm';
import type { GoogleTagManagerData, GTMContactEvent, GTMJobEvent, GTMPageEvent } from '@zr/common/js/gtm/types';

declare const zr: {
  page: {
    model: {
      isLoggedIn: boolean;
      isMobile: boolean;
      gtmData: GoogleTagManagerData;
    };
  };
};

const prepareAndFirePageEvent = (): void => {
  const pageEvent: GTMPageEvent = {
    page: {
      platform: zr.page.model.isMobile ? 'm' : 'd',
      request_id: zr.page.model.gtmData.RequestID,
      pt_id: zr.page.model.gtmData.PersonTrackingID,
      page_type: zr.page.model.gtmData.PageType,
      audience_type: zr.page.model.gtmData.AudienceType,
      location_hint: zr.page.model.gtmData.LocationHint,
      source: '', // Not really used, safe to leave empty
      attribution: {
        rtraffic_source_id: zr.page.model.gtmData.Attribution.RTrafficSourceID,
        rutm_campaign: zr.page.model.gtmData.Attribution.RequestUTMCampaign,
        rutm_medium: zr.page.model.gtmData.Attribution.RequestUTMMedium,
        rutm_source: zr.page.model.gtmData.Attribution.RequestUTMSource,
        vtraffic_source_id: zr.page.model.gtmData.Attribution.VTrafficSourceID,
        vutm_campaign: zr.page.model.gtmData.Attribution.VisitUTMCampaign,
        vutm_medium: zr.page.model.gtmData.Attribution.VisitUTMMedium,
        vutm_source: zr.page.model.gtmData.Attribution.VisitUTMSource,
      },
      first_page_view: {
        show_js_amazon_tag: zr.page.model.gtmData.FirstTimeVisit.AmazonTag,
        show_js_artsai_tag: zr.page.model.gtmData.FirstTimeVisit.ArtsaiTag,
        show_js_data_plus_math_tag: zr.page.model.gtmData.FirstTimeVisit.DataPlusMathTag,
        show_js_ott_tradr_tag: zr.page.model.gtmData.FirstTimeVisit.OTTTradrTag,
        show_js_podsights_tag: zr.page.model.gtmData.FirstTimeVisit.PodsightsTag,
        show_js_samsung_tag: zr.page.model.gtmData.FirstTimeVisit.SamsungTag,
        show_js_tv_scientific_tag: zr.page.model.gtmData.FirstTimeVisit.TVScientificTag,
      },
      search: {
        search: zr.page.model.gtmData.JobTitle,
        location: zr.page.model.gtmData.JobLocation,
        job_locations: zr.page.model.gtmData.JobLocations,
        job_lvks: zr.page.model.gtmData.JobLVKS,
      },
    },
  };

  firePageEvent(pageEvent);
};

const prepareAndFireContactEvent = (): void => {
  const contactEvent: GTMContactEvent = {
    contact: {
      contact_id: zr.page.model.gtmData?.LoggedInUserData?.ContactID,
      country: zr.page.model.gtmData?.LoggedInUserData?.Country,
      md5_email_address: zr.page.model.gtmData?.LoggedInUserData?.MD5Email,
    },
  };

  fireContactEvent(contactEvent);
};

const prepareAndFireJobEvent = (): void => {
  const jobEvent: GTMJobEvent = {
    job: {
      is_external: !zr.page.model.gtmData.CanApplyOnZr,
      title: zr.page.model.gtmData.JobTitle,
      location: zr.page.model.gtmData.JobLocation,
      lvk: zr.page.model.gtmData.LVK,
    },
  };
  if (!zr.page.model.gtmData.IsOjob) {
    jobEvent.job.quiz_id = zr.page.model.gtmData.EncryptedQuizID;
  }

  fireJobEvent(jobEvent);
};

export const initGoogleTagManager = (): void => {
  if (zr.page.model.gtmData) {
    initGTM(zr.page.model.gtmData.ContainerID);

    $(() => {
      prepareAndFirePageEvent();

      const campaignEventAlreadyFired = zr.page.model.gtmData?.ExperianCampaignVisited;
      fireExperianPageViewEvent(campaignEventAlreadyFired);

      // TODO: Logged in != logged in user is a CONTACT, could be an employer???
      if (zr.page.model.isLoggedIn) {
        prepareAndFireContactEvent();
      }

      if (zr.page.model.gtmData.LVK) {
        prepareAndFireJobEvent();
      }
    });
  }
};
